import { encrypt, decrypt } from '@/helpers/encrypt'
import logger from '@/services/logger'

const DB_NAME = 'PROOFOFID'
const TABLE = 'USERS'

// Map API response fields to table expected fields
const mapUserFromApi = (user) => ({
  id: user.id || user.userId || '',
  userId: user.userId || user.id || '',
  userName: user.username || user.userName || user.name || '',
  userEmail: user.email || user.userEmail || '',
  role: user.role || '',
  organizationId: user.organizationId || user.organization_id || '',
  organizationName: user.organizationName || user.organization_name || '',
  reseller_user_id: user.reseller_user_id || user.resellerUserId || user.organizationResellerUserId ||
    user.organization?.reseller_user_id || user.organization?.resellerUserId || '',
  active: user.active ?? user.status === 'Active' ?? true,
  createdAt: user.created_at || user.createdAt || '',
  updatedAt: user.updated_at || user.updatedAt || '',
  auditTrail: user.auditTrail || []
})

export default {
  namespaced: true,
  state: {
    items: []
  },
  getters: {
    getUserById: async (state, dispatch) => async (id) => {
      return await decrypt(state, (await dispatch('get',
        {
          database: DB_NAME,
          table: TABLE,
          id
        },
        { root: true }
      )).data)
    },
    user: async (state, dispatch) => {
      return await decrypt(state, (await dispatch('get',
        {
          database: DB_NAME,
          table: TABLE,
          id: state.auth.authId
        },
        { root: true }
      )).data)
    }
  },
  actions: {
    async get ({ rootState, dispatch }, id) {
      let userId = rootState.auth.authId ? rootState.auth.authId : window.sessionStorage.getItem('USER_ID')
      if (!userId) userId = id
      if (userId) {
        let data = await dispatch('get',
          {
            database: DB_NAME,
            table: TABLE,
            id: userId
          },
          { root: true }
        )
        try {
          if (data.data) {
            data = await decrypt(rootState, data.data, userId)
          } else {
            data = await decrypt(rootState, data, userId)
          }
        } catch (error) {
          throw new Error('Your information was lost due to emptying cache. Please register again.')
        }
        if (data.data) {
          data = data.data
        }
        return data
      }
      return null
    },
    async add ({ commit, getters, dispatch, rootState }, user) {
      return await dispatch('add',
        {
          database: DB_NAME,
          table: TABLE,
          record: { id: user.id, data: await encrypt(rootState, user, user.id) }
        },
        { root: true }
      )
    },
    async update ({ commit, getters, dispatch, rootState }, user) {
      return await dispatch('update',
        {
          database: DB_NAME,
          table: TABLE,
          id: rootState.auth.authId,
          record: { id: rootState.auth.authId, data: encrypt(rootState, user) }
        },
        { root: true }
      )
    },
    async fetchUsersList ({ dispatch, rootState }, { page = 1, limit = 10, sortBy = 'name', order = 'asc' }) {
      try {
        // Get the organization ID from the auth state or current user
        const userRole = rootState.authorization?.userRole
        const organizationId = rootState.authorization?.organizationId

        let response
        if (['bits_admin', 'superadmin'].includes(userRole)) {
          response = await dispatch('fetchDataWithParams', {
            stream: 'api/organizations/members',
            params: { limit, page, sortBy, order }
          }, { root: true })
        } else {
          if (!/^[a-zA-Z0-9\-_]{1,50}$/.test(organizationId)) {
            logger.error('Invalid organization ID format', { organizationId })
            return { users: [], total: 0, error: 'Invalid organization ID' }
          }

          response = await dispatch('fetchDataWithParams', {
            stream: `api/organizations/${encodeURIComponent(organizationId)}/members`,
            params: { limit, page, sortBy, order }
          }, { root: true })
        }

        // Transform response to match expected format
        // API returns { users: [...], totalResults: N } or { data: [...], totalResults: N }
        const usersData = response.users || response.data || []
        const users = Array.isArray(usersData) ? usersData.map(mapUserFromApi) : []
        return {
          users,
          total: response.totalResults || 0,
          page,
          limit
        }
      } catch (error) {
        logger.error('Error fetching users list from API', {
          error: error.message,
          stack: error.stack
        })

        // Return empty result on error but don't throw to prevent UI crashes
        return {
          users: [],
          total: 0,
          error: 'Unable to load users. Please try again or contact support.'
        }
      }
    },
    async searchUsers ({ dispatch }, { query, page = 1, limit = 10, sortBy = 'username', order = 'asc' }) {
      try {
        // Validate the search term. Query-string encoding is handled by fetchDataWithParams.
        if (!query || typeof query !== 'string') {
          logger.warn('Invalid search query provided', { query })
          return { users: [], total: 0, page, limit }
        }

        const normalizedQuery = query.trim()
        if (!normalizedQuery) {
          logger.warn('Empty search query')
          return { users: [], total: 0, page, limit }
        }

        // Valid sortBy values: username, email, role, created_at, updated_at
        const validSortColumns = ['username', 'email', 'role', 'created_at', 'updated_at']
        const safeSortBy = validSortColumns.includes(sortBy) ? sortBy : 'username'

        // Call the search API endpoint: /api/users/search?query=...&sortBy=...&order=...
        const response = await dispatch('fetchDataWithParams', {
          stream: 'api/users/search',
          params: {
            query: normalizedQuery,
            limit,
            page,
            sortBy: safeSortBy,
            order: order.toUpperCase()
          }
        }, { root: true })

        // Transform response to match expected format
        // API returns { users: [...], totalResults: N }
        const usersData = response.users || response.data || []
        const users = Array.isArray(usersData) ? usersData.map(mapUserFromApi) : []
        return {
          users,
          total: response.totalResults || 0,
          page,
          limit
        }
      } catch (error) {
        logger.error('Error searching users from API', {
          error: error.message,
          stack: error.stack,
          query
        })

        // Return empty result on error but don't throw to prevent UI crashes
        return {
          users: [],
          total: 0,
          page,
          limit,
          error: 'Unable to search users. Please try again or contact support.'
        }
      }
    },
    async toggleStatus ({ dispatch, rootState }, { userId, currentStatus }) {
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active'
      const currentUser = await dispatch('get', userId)
      const updatedUser = {
        ...currentUser,
        status: newStatus,
        statusColor: newStatus === 'Active' ? 'success' : 'error'
      }
      return await dispatch('update',
        {
          database: DB_NAME,
          table: TABLE,
          id: userId,
          record: { id: userId, data: await encrypt(rootState, updatedUser, userId) }
        },
        { root: true }
      )
    },
    async deleteUser ({ dispatch }, userId) {
      return await dispatch('del',
        {
          database: DB_NAME,
          table: TABLE,
          id: userId
        },
        { root: true }
      )
    },
    async editUser ({ dispatch, rootState }, { userId, userData }) {
      const currentUser = await dispatch('get', userId)
      const updatedUser = {
        ...currentUser,
        ...userData,
        updatedAt: new Date().toISOString()
      }
      return await dispatch('update',
        {
          database: DB_NAME,
          table: TABLE,
          id: userId,
          record: { id: userId, data: await encrypt(rootState, updatedUser, userId) }
        },
        { root: true }
      )
    }
  },
  mutations: {}
}
